import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { getIcon } from "@/lib/icons";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const META = {
  web: {
    img: "https://images.unsplash.com/photo-1609921212029-bb5a28e60960?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: { de: "Web-Entwicklung", en: "Web Development" },
    sub: { de: "Schnelle, sichere und suchmaschinenoptimierte Websites & Web-Apps mit Next.js und React.", en: "Fast, secure and SEO-optimized websites & web apps with Next.js and React." },
    points: { de: ["Server-Side Rendering & SEO", "Individuelles Premium-Design", "Barrierefrei & responsiv", "Content-Management-System"], en: ["Server-side rendering & SEO", "Custom premium design", "Accessible & responsive", "Content management system"] },
  },
  mobile: {
    img: "https://images.unsplash.com/photo-1551650975-87deedd944c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: { de: "App-Entwicklung", en: "App Development" },
    sub: { de: "Native und plattformübergreifende Apps für iOS und Android mit React Native & Flutter.", en: "Native and cross-platform apps for iOS and Android with React Native & Flutter." },
    points: { de: ["iOS & Android", "Push-Benachrichtigungen", "App Store & Play Store Release", "Offline-fähig"], en: ["iOS & Android", "Push notifications", "App Store & Play Store release", "Offline capable"] },
  },
  software: {
    img: "https://images.unsplash.com/photo-1599837565318-67429bde7162?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: { de: "Unternehmenssoftware", en: "Business Software" },
    sub: { de: "Maßgeschneiderte Software, Admin-Panels und Portale zur Automatisierung Ihrer Prozesse.", en: "Custom software, admin panels and portals to automate your processes." },
    points: { de: ["Prozessautomatisierung", "Rollen & Rechte", "Integrationen & APIs", "Skalierbar & sicher"], en: ["Process automation", "Roles & permissions", "Integrations & APIs", "Scalable & secure"] },
  },
};

export default function CategoryPage({ category }) {
  const { t, lang, pickL } = useI18n();
  const [services, setServices] = useState([]);
  const m = META[category];
  useEffect(() => { api.get("/services", { params: {} }).then((r) => setServices(r.data.filter((s) => s.category === category))).catch(() => {}); }, [category]);
  return (
    <div>
      <PageHero eyebrow={t("nav.services")} title={m.title[lang]} subtitle={m.sub[lang]}>
        <Link to="/quote" className="btn-primary mt-8" data-testid="cat-quote-btn">{t("hero.cta1")} <ArrowRight size={16} /></Link>
      </PageHero>
      <section className="container-x py-14 grid lg:grid-cols-2 gap-10 items-center">
        <img src={m.img} alt={m.title[lang]} className="rounded-2xl border border-border w-full object-cover aspect-[4/3]" />
        <div>
          <h2 className="font-display text-3xl font-black tracking-tight mb-6">{lang === "de" ? "Warum wir?" : "Why us?"}</h2>
          <div className="space-y-3">
            {m.points[lang].map((p, i) => (
              <div key={i} className="flex items-center gap-3"><Check size={18} className="text-primary" /> <span>{p}</span></div>
            ))}
          </div>
        </div>
      </section>
      <section className="container-x pb-16">
        <h2 className="font-display text-2xl font-bold mb-6">{lang === "de" ? "Passende Leistungen" : "Related services"}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.04}>
              <Link to={`/services/${s.slug}`} className="group surface p-7 block h-full hover:border-primary transition-colors" data-testid={`cat-service-${s.slug}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-5">{getIcon(s.icon, { size: 22 })}</div>
                <h3 className="font-display font-bold text-lg mb-2">{pickL(s, "name")}</h3>
                <p className="text-sm text-muted-foreground">{pickL(s, "short")}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
